import React, {
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
} from "react";
import SimpleSlider from "./SliderCustom";
// import SimpleSlider from "./SimpleSlider";
import styled, { css } from "styled-components";
import AudioPlay from "./AudioPlay";
import { useAudios } from "../hooks/useAudios";
import { useImages } from "../hooks/useImages";
import InputTextArea from "./InputTextArea";
import { ReactComponent as CheckSVG } from "../assets/svg/check.svg";
import { ReactComponent as ReviewSVG } from "../assets/svg/review.svg";
import clean from "clean-deep";
import { useAttempt } from "../hooks/useAttempt";
import { useLessonDetails } from "../hooks/useLessonDetails";
import Button from "./Button";
import ModalNotification from "./ModalNotification";
import LessonAPI from "../apis/LessonAPI";
import Editor from "./Editor";
import Advice from "./atoms/Advice";

const LessonDataCarta = ({
  lessonId = undefined,
  disabled = false,
  hasNextLesson = false,
  goToNextLesson = () => {},
  backToEpisode = () => {},
  onSuccess = () => {},
  courseId,
  episodeId,
  chapterId,
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSendedModal, setShowSendedModal] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);

  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const lesson = useLessonDetails(lessonId);
  const description = lesson?.description;
  const attempt = useAttempt(lessonId);
  const state = attempt?.state || "NOT_SENDED";

  const [text, setText] = useState("");

  useEffect(() => {
    if (!attempt) return;

    setText(attempt.text || "");
  }, [attempt]);

  const isReady = text.length > 0;

  const sendAttempt = () => {
    setIsSending(true);

    LessonAPI.LESSON_CARTA.sendAttempt(lessonId, {
      text,
      courseId,
      episodeId,
      chapterId,
    })
      .then(() => {
        setIsSending(false);
        setShowConfirmModal(false);
        setShowSendedModal(true);
        onSuccess();
      })
      .catch(() => {
        setIsSending(false);
      });
  };

  const save = () => {
    setIsSaving(true);
    LessonAPI.LESSON_CARTA.save(lessonId, { text })
      .then(() => {
        setIsSaving(false);
        setShowSavedModal(true);
      })
      .catch(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <Container>
        {state === "FAILURE" && (
          <>
            <Advice
              text="Edita y reenvía tu carta. Recuerda que puedes editar tu carta."
              type="info"
            />
            <br />
          </>
        )}
        {description && (
          <Description
            as={Editor}
            theme={null}
            isMinimal={true}
            readOnly={true}
            value={description}
          />
        )}
        <Content>
          {state === "IN_REVIEW" && (
            <MessageWrapper>
              <ReviewIconSVG as={ReviewSVG} />
              <TextSuccess>
                {`La carta está siendo evaluada.\nRecibirás una notificación cuando la evaluación haya terminado.`}
              </TextSuccess>
            </MessageWrapper>
          )}
          {state === "SUCCESS" && (
            <MessageWrapper>
              <CheckIconSVG as={CheckSVG} />
              <TextSuccess>La carta está escrita correctamente!</TextSuccess>
            </MessageWrapper>
          )}
          {state === "FAILURE" && attempt?.observations && (
            <ObservationsWrapper>
              <Title>Observaciones</Title>
              <Observations
                as={Editor}
                theme={null}
                isMinimal={true}
                readOnly={true}
                value={attempt.observations}
              />
            </ObservationsWrapper>
          )}
          <InputWrapper state={state}>
            <InputTextAreaStyled
              as={Editor}
              theme={null}
              value={text}
              onChange={setText}
              placeholder="Escribe aquí la carta..."
              readOnly={
                disabled || state === "SUCCESS" || state === "IN_REVIEW"
              }
              minHeight={200}
            />
          </InputWrapper>

          {/* <InputTextAreaStyled
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-placeholder="Escribe aquí..."
            spellCheck="false"
            disabled={disabled || state === "SUCCESS" || state === "IN_REVIEW"}
            state={state}
          /> */}
        </Content>
        {state !== "SUCCESS" && state !== "IN_REVIEW" && (
          <Footer>
            <ButtonSave text="Guardar" onClick={save} loading={isSaving} />
            <ButtonStyled
              text="Enviar carta"
              disabled={!isReady}
              onClick={() => {
                if (isReady) {
                  setShowConfirmModal(true);
                }
              }}
            />
          </Footer>
        )}
        {(state === "SUCCESS" || state === "IN_REVIEW") && (
          <Footer>
            {hasNextLesson ? (
              <ButtonStyled
                primary={true}
                text="Ir al siguiente ejercicio"
                onClick={goToNextLesson}
              />
            ) : (
              <ButtonStyled
                primary={true}
                text="Volver a los capítulos"
                onClick={backToEpisode}
              />
            )}
          </Footer>
        )}
      </Container>

      {showSavedModal && (
        <ModalNotification
          isOpen={true}
          title="Guardado"
          description={`La carta se guardó correctamente.`}
          type="success"
          buttons={
            <>
              <Button
                primary={true}
                text="Aceptar"
                onClick={() => setShowSavedModal(false)}
              />
            </>
          }
        />
      )}

      {showConfirmModal && (
        <ModalNotification
          isOpen={true}
          title="Confirmación"
          description={`¿Estás seguro de enviar la carta?`}
          type="question"
          buttons={
            <>
              <Button
                secondary={true}
                text="Cancelar"
                onClick={() => setShowConfirmModal(false)}
              />
              <Button
                primary={true}
                text="Enviar"
                loading={isSending}
                onClick={() => sendAttempt()}
              />
            </>
          }
        />
      )}

      {showSendedModal && (
        <ModalNotification
          isOpen={true}
          title="Carta en evaluación"
          description={`Los docentes de RaDi evaluarán tu carta en las próximas horas.\n\nRecibirás una notificación cuando hayan terminado de revisar tu carta.`}
          type="information"
          buttons={
            <>
              <Button
                secondary={true}
                text="Cerrar"
                onClick={() => setShowSendedModal(false)}
              />
              {hasNextLesson ? (
                <Button
                  primary={true}
                  text="Ir al siguiente ejercicio"
                  onClick={goToNextLesson}
                />
              ) : (
                <Button
                  primary={true}
                  text="Volver a los capítulos"
                  onClick={backToEpisode}
                />
              )}
            </>
          }
        />
      )}
    </>
  );
};

const Description = styled.div`
  font-size: 0.9em;
  color: white !important;
  background-color: transparent !important;

  & * {
    color: white !important;
    background-color: transparent !important;
  }

  margin-bottom: 25px;
  /* white-space: pre-wrap; */
`;

const Content = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 15px;
  }
`;

const Container = styled.div``;

const MessageWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  padding: 15px;
  background-color: white;
  border-radius: 15px;
`;

const CheckIconSVG = styled.div`
  width: 25px;
  height: 25px;
  min-width: 25px;
  min-height: 25px;
`;

const ReviewIconSVG = styled.div`
  width: 25px;
  height: 25px;
  min-width: 25px;
  min-height: 25px;
  fill: #607d8b;
`;

const TextSuccess = styled.div`
  margin-left: 15px;
  white-space: pre-wrap;
`;

const ObservationsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding: 15px;
  background-color: white;
  border-radius: 15px;
`;

const Title = styled.div`
  font-size: 1em;
  font-weight: bold;
  color: red;

  margin-bottom: 10px;
`;

const Observations = styled.div`
  font-size: 0.9em;

  color: black !important;
  background-color: transparent !important;

  & * {
    color: black !important;
    background-color: transparent !important;
  }
`;

const InputWrapper = styled.div`
  ${(props) => {
    if (props.state === "FAILURE")
      return css`
        & b,
        & strong {
          color: black;
        }
      `;
    else
      return css`
        & b,
        & strong {
          color: black;
          font-weight: normal;
        }
      `;
  }}
`;

const InputTextAreaStyled = styled.div`
  /* min-height: 250px; */

  border-radius: 15px;
  background-color: white;
`;

const Footer = styled.div`
  display: flex;
  margin-top: 25px;

  & > *:not(:last-child) {
    margin-right: 15px;
  }
`;

const ButtonStyled = styled(Button)`
  .button {
    background-color: rgba(33, 150, 243, 0.85);
    color: white;
  }

  flex: 2 0 0;
`;

const ButtonSave = styled(Button)`
  .button {
    background-color: rgba(0, 0, 0, 0.1);
    color: white;
  }

  flex: 1 0 0;
`;

export default LessonDataCarta;
